/**
 * Submits a queued scan as `claimCodeOnBehalf` (POST /v1/ops/claims) with the scan's own
 * Idempotency-Key, tenant (`X-Tenant-Id`), outlet and `scannedAt`, never the currently
 * selected ones. Uses the staff API client, so the bearer token is read (and renewed
 * silently when expired) per request. Maps the response to a {@link SubmitOutcome}
 * including `Retry-After` (seconds or HTTP date) for 429/503.
 */
import type { ApiClient } from '@macksons/contracts';
import { asApiError, toApiError, type ApiError } from '@macksons/web-client';
import type { ClaimTransport, ScanProblem } from './scan-queue';

export function parseRetryAfter(value: string | null, nowMs: number): number | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
  const at = Date.parse(trimmed);
  return Number.isNaN(at) ? undefined : Math.max(0, at - nowMs);
}

function problemOf(err: ApiError): ScanProblem {
  return {
    status: err.status,
    code: err.code,
    ...(err.title === undefined ? {} : { title: err.title }),
    ...(err.detail === undefined ? {} : { detail: err.detail }),
    ...(err.traceId === undefined ? {} : { traceId: err.traceId }),
  };
}

export function createClaimTransport(client: ApiClient, now: () => number = Date.now): ClaimTransport {
  return async (scan) => {
    try {
      const { data, error, response } = await client.POST('/ops/claims', {
        params: { header: { 'X-Tenant-Id': scan.tenantId, 'Idempotency-Key': scan.idempotencyKey } },
        body: { memberId: scan.memberId, outletId: scan.outletId, shortCode: scan.shortCode, scannedAt: scan.scannedAt },
      });
      if (response.ok && data) return { ok: true, result: data };
      const retryAfterMs = parseRetryAfter(response.headers.get('Retry-After'), now());
      return {
        ok: false,
        problem: problemOf(toApiError(response.status, error)),
        ...(retryAfterMs === undefined ? {} : { retryAfterMs }),
      };
    } catch (cause) {
      return { ok: false, problem: problemOf(asApiError(cause)) };
    }
  };
}
